import React from 'react';
import { Link } from 'react-router-dom';
import Pattern from '../components/Pattern';

export default function FreeTrial() {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-primary-50 to-gray-50">
          <div className="absolute inset-0 opacity-10">
            <Pattern />
          </div>
        </div>
        <div className="relative pt-6 pb-16 sm:pb-24">
          <nav className="relative max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6">
            <div className="flex items-center flex-1">
              <div className="flex items-center justify-between w-full">
                <Link to="/">
                  <span className="sr-only">Horse Racing Oracle</span>
                  <img
                    src="/HRO-Logo-transparent-white-logo.png"
                    alt="HRO Logo"
                    className="h-12"
                  />
                </Link>
                <div className="flex items-center">
                  <div className="space-x-4">
                    <Link
                      to="/login"
                      className="btn btn-primary"
                    >
                      Sign In
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </nav>

          <main className="mt-16 mx-auto max-w-7xl px-4 sm:mt-24">
            <div className="text-center relative">
              <div className="absolute inset-0 flex items-center justify-center opacity-5">
                <svg className="w-96 h-96" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="50" cy="50" r="45" stroke="currentColor" strokeWidth="2"/>
                  <path d="M50 5 L50 95 M5 50 L95 50" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
              <h1 className="relative text-4xl tracking-tight font-extrabold text-gray-900 sm:text-5xl md:text-6xl leading-relaxed">
                <span className="block bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-700 mb-4">Intelligent Horse Racing</span>
                <span className="block bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-700 mb-4">Market Analysis & Bet Placement</span>
                <span className="block bg-clip-text text-transparent bg-gradient-to-r from-primary-600 to-primary-500">100% on Auto-Pilot</span>
              </h1>
              <p className="mt-3 max-w-md mx-auto text-base text-gray-500 sm:text-lg md:mt-10 md:text-xl md:max-w-3xl relative z-10">
                Betting on <b>favourites</b> with confidence has never been this easy.
                Smart betting powered by <b>strict parameters and strong risk management</b>.
                Identify <b>high-confidence opportunities</b> and achieve consistent, daily returns...<b><u>on auto-pilot</u></b>.
              </p>
              <div className="mt-5 sm:mt-8 flex justify-center relative z-20">
                <div className="rounded-md shadow">
                  <a
                    href="https://betoracle.pay.clickbank.net/?cbitems=2&template=HRO"
                    className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 md:py-4 md:text-lg md:px-10"
                  >
                    Start Your 7 Day FREE Trial
                  </a>
                </div>
              </div>
            </div>
          </main>

          <div className="mt-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6">
              {/* What is Horse Racing Oracle Section */}
              <div className="mb-24">
                <h2 className="text-3xl font-extrabold text-gray-900 text-center mb-12">
                  What is Horse Racing Oracle?
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                  <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100">
                    <div className="prose prose-lg max-w-none">
                      <p className="text-lg text-gray-600 leading-relaxed">
                        Horse Racing Oracle is an <span className="text-primary-600 font-semibold">intelligent automated betting bot</span> that takes the complexity out of horse race betting. Using advanced algorithms and real-time data analysis, it identifies and places bets on strong favorites with the highest probability of success.
                      </p>
                      <div className="mt-6 space-y-4">
                        <div className="flex items-start">
                          <svg className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          <p className="ml-3 text-gray-600">Set your daily profit target (up to 2%)</p>
                        </div>
                        <div className="flex items-start">
                          <svg className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          <p className="ml-3 text-gray-600">Bot automatically analyses and places bets</p>
                        </div>
                        <div className="flex items-start">
                          <svg className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          <p className="ml-3 text-gray-600">Built-in risk management and loss protection</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-6">
                    <div className="bg-gradient-to-br from-primary-50 to-white p-6 rounded-xl shadow-sm border border-primary-100">
                      <h3 className="text-xl font-semibold text-primary-900 mb-2">Smart Analysis</h3>
                      <p className="text-gray-600">Analyses multiple factors including odds, ratings, and form data</p>
                    </div>
                    <div className="bg-gradient-to-br from-primary-50 to-white p-6 rounded-xl shadow-sm border border-primary-100">
                      <h3 className="text-xl font-semibold text-primary-900 mb-2">Auto Betting</h3>
                      <p className="text-gray-600">Places bets automatically when conditions match criteria</p>
                    </div>
                    <div className="bg-gradient-to-br from-primary-50 to-white p-6 rounded-xl shadow-sm border border-primary-100">
                      <h3 className="text-xl font-semibold text-primary-900 mb-2">Risk Control</h3>
                      <p className="text-gray-600">Implements strict stake sizing and profit targets</p>
                    </div>
                    <div className="bg-gradient-to-br from-primary-50 to-white p-6 rounded-xl shadow-sm border border-primary-100">
                      <h3 className="text-xl font-semibold text-primary-900 mb-2">24/7 Operation</h3>
                      <p className="text-gray-600">Runs continuously during active racing hours</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
                <div className="text-center group">
                  <div className="flex items-center justify-center h-16 w-16 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-600 text-white mx-auto transform transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg">
                    <svg className="h-8 w-8 transform transition-transform duration-300 group-hover:scale-110" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <h3 className="mt-2 text-xl font-semibold text-gray-900 transition-colors duration-300 group-hover:text-primary-600">Advanced Analysis</h3>
                  <p className="mt-2 text-base text-gray-500">
                    Sophisticated algorithms analyse race data to identify qualifying races and strong favorites.
                  </p>
                </div>

                <div className="text-center group">
                  <div className="flex items-center justify-center h-16 w-16 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-600 text-white mx-auto transform transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg">
                    <svg className="h-8 w-8 transform transition-transform duration-300 group-hover:scale-110" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <h3 className="mt-2 text-xl font-semibold text-gray-900 transition-colors duration-300 group-hover:text-primary-600">Automated Execution</h3>
                  <p className="mt-2 text-base text-gray-500">
                    Set your daily profit targets and let our bot execute trades with precision timing.
                  </p>
                </div>

                <div className="text-center group">
                  <div className="flex items-center justify-center h-16 w-16 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-600 text-white mx-auto transform transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg">
                    <svg className="h-8 w-8 transform transition-transform duration-300 group-hover:scale-110" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <h3 className="mt-2 text-xl font-semibold text-gray-900 transition-colors duration-300 group-hover:text-primary-600">Risk Management</h3>
                  <p className="mt-2 text-base text-gray-500">
                    Built-in safeguards and configurable limits to protect your investment.
                  </p>
                </div>
              </div>

              {/* Comparison Section */}
              <div className="mt-16 max-w-7xl mx-auto px-4 sm:px-6 py-16">
                <h2 className="text-3xl font-extrabold text-gray-900 text-center mb-12">
                  Why Choose Horse Racing Oracle?
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Manual Betting</h3>
                    <ul className="space-y-3">
                      <li className="flex items-center text-gray-500">
                        <svg className="h-5 w-5 text-red-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        Time-consuming research
                      </li>
                      <li className="flex items-center text-gray-500">
                        <svg className="h-5 w-5 text-red-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        Emotional decisions
                      </li>
                      <li className="flex items-center text-gray-500">
                        <svg className="h-5 w-5 text-red-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        Inconsistent results
                      </li>
                      <li className="flex items-center text-gray-500">
                        <svg className="h-5 w-5 text-red-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        No automation
                      </li>
                    </ul>
                  </div>
                  <div className="bg-primary-50 p-6 rounded-lg shadow-sm border border-primary-100">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Horse Racing Oracle</h3>
                    <ul className="space-y-3">
                      <li className="flex items-center text-gray-700">
                        <svg className="h-5 w-5 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        AI-powered analysis
                      </li>
                      <li className="flex items-center text-gray-700">
                        <svg className="h-5 w-5 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Data-driven decisions
                      </li>
                      <li className="flex items-center text-gray-700">
                        <svg className="h-5 w-5 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Consistent strategy
                      </li>
                      <li className="flex items-center text-gray-700">
                        <svg className="h-5 w-5 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Fully automated
                      </li>
                    </ul>
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Tipster Services</h3>
                    <ul className="space-y-3">
                      <li className="flex items-center text-gray-500">
                        <svg className="h-5 w-5 text-red-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        Manual bet placement
                      </li>
                      <li className="flex items-center text-gray-500">
                        <svg className="h-5 w-5 text-red-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        Human bias
                      </li>
                      <li className="flex items-center text-gray-500">
                        <svg className="h-5 w-5 text-red-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        Variable quality
                      </li>
                      <li className="flex items-center text-gray-500">
                        <svg className="h-5 w-5 text-red-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        Limited availability
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Additional Features */}
              <div className="mt-0 max-w-7xl mx-auto px-4 sm:px-6 py-16">
                <h2 className="text-3xl font-extrabold text-gray-900 text-center mb-12">
                  Key Features
                </h2>
                <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
                  <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Qualifying Race Detection</h3>
                    <p className="text-gray-500">
                      Our algorithm identifies races with optimal conditions for successful betting.
                    </p>
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Confidence Levels</h3>
                    <p className="text-gray-500">
                      Clear categorization of opportunities as Very Strong, Strong, or Standard Favourites.
                    </p>
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Real-time Tracking</h3>
                    <p className="text-gray-500">
                      Monitor your performance with comprehensive statistics and live updates.
                    </p>
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Betfair Integration</h3>
                    <p className="text-gray-500">
                      Seamless connection with your Betfair account for automated trading.
                    </p>
                  </div>
                </div>
              </div>

              {/* How It Works Section */}
              <div className="mt-16 max-w-7xl mx-auto px-4 sm:px-6 py-16 bg-gradient-to-b from-white to-gray-50 rounded-3xl shadow-sm">
                <h2 className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-gray-900 to-gray-700 text-center mb-12">
                  How It Works
                </h2>
                <div className="grid grid-cols-1 gap-8 md:grid-cols-4 relative">
                  <div className="relative group">
                    <div className="flex items-center justify-center h-16 w-16 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-600 text-white mx-auto mb-4 transform transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg">
                      <span className="text-xl font-bold">1</span>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 text-center mb-2 transition-colors duration-300 group-hover:text-primary-600">Sign Up & Connect</h3>
                    <p className="text-gray-600 text-center">
                      Create your account and connect your Betfair account securely
                    </p>
                    {/* Connector line */}
                    <div className="hidden md:block absolute top-8 left-full w-full h-0.5 bg-gradient-to-r from-primary-200 to-primary-300 transform -translate-x-4"></div>
                  </div>
                  <div className="relative group">
                    <div className="flex items-center justify-center h-16 w-16 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-600 text-white mx-auto mb-4 transform transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg">
                      <span className="text-xl font-bold">2</span>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 text-center mb-2 transition-colors duration-300 group-hover:text-primary-600">Set Your Target</h3>
                    <p className="text-gray-600 text-center">
                      Choose your daily profit target (up to 2% of your balance)
                    </p>
                    {/* Connector line */}
                    <div className="hidden md:block absolute top-8 left-full w-full h-0.5 bg-gradient-to-r from-primary-200 to-primary-300 transform -translate-x-4"></div>
                  </div>
                  <div className="relative group">
                    <div className="flex items-center justify-center h-16 w-16 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-600 text-white mx-auto mb-4 transform transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg">
                      <span className="text-xl font-bold">3</span>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 text-center mb-2 transition-colors duration-300 group-hover:text-primary-600">Activate the Bot</h3>
                    <p className="text-gray-600 text-center">
                      Let our AI analyze races and place bets automatically
                    </p>
                    {/* Connector line */}
                    <div className="hidden md:block absolute top-8 left-full w-full h-0.5 bg-gradient-to-r from-primary-200 to-primary-300 transform -translate-x-4"></div>
                  </div>
                  <div className="relative group">
                    <div className="flex items-center justify-center h-16 w-16 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-600 text-white mx-auto mb-4 transform transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg">
                      <span className="text-xl font-bold">4</span>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 text-center mb-2 transition-colors duration-300 group-hover:text-primary-600">Track & Profit</h3>
                    <p className="text-gray-600 text-center">
                      Monitor your performance and withdraw profits anytime
                    </p>
                  </div>
                </div>
              </div>

              {/* Pricing Section */}
              <div className="mt-16 max-w-7xl mx-auto px-4 sm:px-6 py-16">
                <h2 className="text-3xl font-extrabold text-gray-900 text-center mb-12">
                  Start Winning Today
                </h2>
                <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
                  <div className="px-6 py-8 bg-primary-500 sm:p-10 sm:pb-6">
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 items-center text-center">
                      <div>
                        <h3 className="text-2xl leading-9 font-extrabold text-white">
                          7-Day FREE Trial
                        </h3>
                        <div className="mt-4 flex items-center justify-center text-white">
                          <span className="text-5xl font-extrabold">£0</span>
                        </div>
                      </div>
                      <div className="text-white">
                        <p className="text-lg">Then only</p>
                        <p className="text-3xl font-bold">£97/month</p>
                        <p className="text-sm mt-2">Cancel anytime</p>
                      </div>
                    </div>
                  </div>
                  <div className="px-6 pt-6 pb-8 bg-white sm:p-10">
                    <ul className="space-y-4 max-w-md mx-auto text-center">
                      {[
                        'Immediate access to web app',
                        'Full access to automated betting bot',
                        'Real-time performance tracking',
                        'Daily profit targeting up to 2%',
                        'Secure Betfair integration',
                        '24/7 technical support',
                        '14-day money-back guarantee'
                      ].map((feature) => (
                        <li key={feature} className="flex items-center justify-center">
                          <div className="flex-shrink-0">
                            <svg className="h-6 w-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                          <p className="ml-3 text-base text-gray-700">{feature}</p>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-8">
                      <a
                        href="https://betoracle.pay.clickbank.net/?cbitems=2&template=HRO"
                        className="w-full flex items-center justify-center px-6 py-4 border border-transparent text-xl font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 text-center"
                      >
                        Start Your FREE Trial
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* FAQ Section */}
              <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 bg-gray-50">
                <h2 className="text-3xl font-extrabold text-gray-900 text-center mb-12">
                  Frequently Asked Questions
                </h2>
                <div className="max-w-3xl mx-auto divide-y divide-gray-200">
                  {[
                    {
                      question: "What is HorseRacingOracle?",
                      answer: "HorseRacingOracle is an automated betting bot that uses advanced algorithms to identify and place bets on qualifying horse races. It analyses multiple factors including odds, ratings, and form to make informed betting decisions, aiming to generate consistent profits while managing risk."
                    },
                    {
                      question: "Once I purchase, how will I receive access to HorseRacingOracle?",
                      answer: "After your purchase, you'll be redirected to a thank you page where you can create your account and start using HorseRacingOracle immediately."
                    },
                    {
                      question: "What is a daily profit target?",
                      answer: "A daily profit target is a limit you set on how much profit the bot will aim to make in a single day. This helps manage your bankroll and ensures sustainable, long-term trading. Once the daily target is reached, the bot will stop placing bets for that day."
                    },
                    {
                      question: "How does the bot select races to bet on?",
                      answer: "The bot analyses multiple factors including number of runners, odds, official ratings, and recent form to identify qualifying races with strong favorites."
                    },
                    {
                      question: "What is the maximum daily profit target?",
                      answer: "To help protect your balance and manage your funds, our system allows a maximum daily profit target of 2% of your account balance."
                    },
                    {
                      question: "How do I connect my Betfair account?",
                      answer: "Go to the 'Connect Betfair Account' page and follow the authentication process. You'll need to authorise our HorseRacingOracle application to place bets on your behalf."
                    },
                    {
                      question: "What happens if I lose internet connection?",
                      answer: "The bot will continue to run even if your internet cuts off or your computer is switched off. In the unlikely scenario where we have issues connecting to Betfair's servers then the bot will stop operating until connection is restored."
                    },
                    {
                      question: "Can I run the bot 24/7?",
                      answer: "Yes, the bot can run continuously, but it will only place bets during active racing hours and when qualifying races are found."
                    }
                  ].map((faq, index) => (
                    <div key={index} className="py-6">
                      <h3 className="text-lg font-medium text-gray-900">
                        {faq.question}
                      </h3>
                      <p className="mt-2 text-base text-gray-600">
                        {faq.answer}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Call to Action Section */}
              <div className="mt-8 bg-primary-500 py-16">
                <div className="max-w-4xl mx-auto text-center px-4 sm:px-6">
                  <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-6">
                    Ready to Start Winning?
                  </h2>
                  <p className="text-lg sm:text-xl text-white mb-8 max-w-2xl mx-auto">
                    Join successful traders who are already using Horse Racing Oracle to achieve consistent returns in horse racing markets.
                  </p>
                  <a
                    href="https://betoracle.pay.clickbank.net/?cbitems=2&template=HRO"
                    className="inline-block px-8 py-4 text-lg sm:text-xl font-semibold rounded-md bg-white text-primary-600 hover:bg-gray-100 transition-colors duration-150 shadow-lg hover:shadow-xl"
                  >
                    Start Your 7 Day FREE Trial
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 